"use client";
import { useCommand } from "../command/command.js";
import { CheckboxCheckedContext } from "../checkbox/checkbox-checked-context.js";
import { useCheckboxContext } from "../checkbox/checkbox-context.js";
import { createElement, createHook, forwardRef, useEvent, useForceUpdate, useMergeRefs, useTagName, useWrapElement } from "@ariakit/react-utils";
import { disabledFromProps, removeUndefinedValues } from "@ariakit/utils";
import { useEffect, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
//#region src/checkbox/__utils.ts
function getPrimitiveValue(value) {
	if (Array.isArray(value)) return value.toString();
	return value;
}
//#endregion
//#region src/checkbox/checkbox.tsx
const TagName = "input";
function setMixed(element, mixed) {
	if (mixed) element.indeterminate = true;
	else if (element.indeterminate) element.indeterminate = false;
}
function isNativeCheckbox(tagName, type) {
	return tagName === "input" && (!type || type === "checkbox");
}
/**
* Returns props to create a `Checkbox` component. If the element is not a
* native checkbox, the hook will return additional props to make sure it's
* accessible.
* @see https://ariakit.com/components/checkbox
* @example
* ```jsx
* const props = useCheckbox({ render: <div /> });
* <Role {...props}>Accessible checkbox</Role>
* ```
*/
const useCheckbox = createHook(function useCheckbox({ store, name, value: valueProp, checked: checkedProp, defaultChecked, ...props }) {
	const context = useCheckboxContext();
	store = store || context;
	const [_checked, setChecked] = useState(defaultChecked ?? false);
	const checked = useStoreState(store, (state) => {
		if (checkedProp !== void 0) return checkedProp;
		if (state?.value === void 0) return _checked;
		if (valueProp != null) {
			if (Array.isArray(state.value)) {
				const primitiveValue = getPrimitiveValue(valueProp);
				return state.value.includes(primitiveValue);
			}
			return state.value === valueProp;
		}
		if (Array.isArray(state.value)) return false;
		if (typeof state.value === "boolean") return state.value;
		return false;
	});
	const ref = useRef(null);
	const nativeCheckbox = isNativeCheckbox(useTagName(ref, TagName), props.type);
	const mixed = checked ? checked === "mixed" : void 0;
	const isChecked = checked === "mixed" ? false : checked;
	const disabled = disabledFromProps(props);
	const [propertyUpdated, schedulePropertyUpdate] = useForceUpdate();
	useEffect(() => {
		const element = ref.current;
		if (!element) return;
		setMixed(element, mixed);
		if (nativeCheckbox) return;
		element.checked = isChecked;
		if (name !== void 0) element.name = name;
		if (valueProp !== void 0) element.value = String(valueProp);
	}, [
		propertyUpdated,
		mixed,
		nativeCheckbox,
		isChecked,
		name,
		valueProp
	]);
	const onChangeProp = props.onChange;
	const onChange = useEvent((event) => {
		if (disabled) {
			event.stopPropagation();
			event.preventDefault();
			return;
		}
		setMixed(event.currentTarget, mixed);
		if (!nativeCheckbox) {
			event.currentTarget.checked = !event.currentTarget.checked;
			schedulePropertyUpdate();
		}
		onChangeProp?.(event);
		if (event.defaultPrevented) return;
		const elementChecked = event.currentTarget.checked;
		setChecked(elementChecked);
		store?.setValue((prevValue) => {
			if (valueProp == null) return elementChecked;
			const primitiveValue = getPrimitiveValue(valueProp);
			if (!Array.isArray(prevValue)) return prevValue === primitiveValue ? false : primitiveValue;
			if (elementChecked) {
				if (prevValue.includes(primitiveValue)) return prevValue;
				return [...prevValue, primitiveValue];
			}
			return prevValue.filter((v) => v !== primitiveValue);
		});
	});
	const onClickProp = props.onClick;
	const onClick = useEvent((event) => {
		onClickProp?.(event);
		if (event.defaultPrevented) return;
		if (nativeCheckbox) return;
		onChange(event);
	});
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(CheckboxCheckedContext.Provider, {
		value: isChecked,
		children: element
	}), [isChecked]);
	props = {
		role: !nativeCheckbox ? "checkbox" : void 0,
		type: nativeCheckbox ? "checkbox" : void 0,
		"aria-checked": checked,
		...props,
		ref: useMergeRefs(ref, props.ref),
		onChange,
		onClick
	};
	props = useCommand({
		clickOnEnter: !nativeCheckbox,
		...props
	});
	return removeUndefinedValues({
		name: nativeCheckbox ? name : void 0,
		value: nativeCheckbox ? valueProp : void 0,
		checked: isChecked,
		...props
	});
});
/**
* Renders an accessible checkbox element. If the underlying element is not a
* native checkbox, this component will pass additional attributes to make sure
* it's accessible.
* @see https://ariakit.com/components/checkbox
* @example
* ```jsx
* <Checkbox render={<div />}>Accessible checkbox</Checkbox>
* ```
*/
const Checkbox = forwardRef(function Checkbox(props) {
	return createElement(TagName, useCheckbox(props));
});
//#endregion
export { useCheckbox as n, getPrimitiveValue as r, Checkbox as t };

//# sourceMappingURL=Bz1czXRN.js.map